"use client";

import { ChevronDown, Terminal, X } from "lucide-react";
import type { ReactNode, Ref } from "react";

import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

/**
 * The closed run trace: "Agent activity · 14 updates · 23.0s" while a run
 * works, "How I answered · 23.0s" once it has finished. TaskRunActivity
 * renders it for live runs; it holds no state, so a fixture run renders the
 * same pill.
 */
export function RunTracePill(props: {
  label: string;
  updateCountLabel: string;
  durationLabel?: string | null;
  running?: boolean;
  onClick?: () => void;
  className?: string;
}) {
  return (
    <Button
      type="button"
      variant="outline"
      size="sm"
      onClick={props.onClick}
      data-running={props.running ? "true" : undefined}
      className={cn(
        "mlx-run-trace-pill h-7 rounded-lg bg-surface/80 text-xs text-muted-foreground shadow-sm",
        props.className,
      )}
    >
      <Terminal className="size-3.5" />
      {props.label}
      {props.running ? (
        <span className="text-tertiary-foreground">· {props.updateCountLabel}</span>
      ) : null}
      {props.durationLabel ? (
        <span className="text-tertiary-foreground">· {props.durationLabel}</span>
      ) : null}
      <ChevronDown className="size-3.5" />
    </Button>
  );
}

/** The row the closed pill sits in, above the composer. */
export function RunTraceRow(props: {
  ref?: Ref<HTMLDivElement>;
  className?: string;
  children: ReactNode;
}) {
  return (
    <div
      ref={props.ref}
      className={cn(
        "flex w-full max-w-[800px] scroll-mb-[20rem] items-center justify-start",
        props.className,
      )}
    >
      {props.children}
    </div>
  );
}

/**
 * The open trace: on a phone the pill stays (it opens the trace in a
 * sheet); from 1024px the terminal takes its place.
 */
export function RunTraceOpenFrame(props: {
  ref?: Ref<HTMLDivElement>;
  className?: string;
  pill: ReactNode;
  terminal: ReactNode;
  /** The phone's trace sheet. */
  children?: ReactNode;
}) {
  return (
    <div
      ref={props.ref}
      className={cn("w-full max-w-[800px] scroll-mb-[20rem]", props.className)}
    >
      <div className="flex items-center justify-start lg:hidden">{props.pill}</div>
      <div className="hidden lg:block">{props.terminal}</div>
      {props.children}
    </div>
  );
}

/** The open trace's footer: status, counts and the close control. */
export function RunTraceFooter(props: {
  status: string;
  countsLabel: string;
  closeLabel: string;
  onClose?: () => void;
}) {
  return (
    <div className="flex min-h-6 items-center gap-2 text-xs text-muted-foreground">
      <Terminal className="size-3.5 shrink-0" />
      <span className="min-w-0 flex-1 truncate">{props.status}</span>
      <span className="hidden shrink-0 text-tertiary-foreground sm:inline">
        {props.countsLabel}
      </span>
      <Button
        type="button"
        variant="ghost"
        size="icon-xs"
        aria-label={props.closeLabel}
        className="-mr-1 text-muted-foreground hover:text-foreground"
        onClick={props.onClose}
      >
        <X className="size-3.5" />
      </Button>
    </div>
  );
}
